<template>
  <div>
    <div class="base-demand" :style="demandCardStyle" @click.once="handleClick">
      <div class="base-demand__header">
        <div class="base-demand__header__top">
          <span class="base-demand__header__top__chip" :style="contractStyle">{{ contract }}</span>
          <slot name="header-top-right" />
        </div>
        <span class="base-demand__header__qualification" :style="qualificationStyle">{{ demand.getQualificationLabel() }}</span>
      </div>
      <div class="base-demand__card">
        <div v-if="isOverlayActive" class="dashboard-recruitment-overlay" :style="overlayStyle">
          <slot name="overlay" />
        </div>
        <slot name="top" />
        <section class="base-demand__card__middle">
          <slot name="middle" />
        </section>
        <slot name="bottom" />
      </div>
    </div>
    <div class="expiration-date">
      <span v-if="!hideExpiration">{{ demandExpirationText }}</span>
      <a v-if="hideExpiration && showDetailsLink" class="link" @click.stop="openRecap">
        {{ $t("ETAB.PAGES.RECRUITMENT.TILE.@OPEN_DETAILS") }}
      </a>
    </div>
  </div>
</template>

<script>
  import { colors } from "quasar";
  import { DEMAND_TYPES } from "@core/constants";
  import { DatesHelpers } from "@core/helpers";
  import { RecruitmentDashboardModel } from "@core/models";
  import { ACTION_DASHBOARD_OPEN_RECRUITMENT_RECAP_MODAL } from "@etab/stores/types";
  import { useDashboardStore } from "@etab/stores/dashboard";

  // we show expiration clock for more than one day
  const EXPIRATION_TIME_REAL_TIME_THRESHOLD = 3 * 24 * 3600;

  export default {
    props: {
      demand: {
        type: Object,
        default: () => new RecruitmentDashboardModel(),
      },
      color: {
        type: String,
        required: true,
      },
      demandColor: {
        type: String,
        default: "",
      },
      hideExpiration: {
        type: Boolean,
        default: false,
      },
      isOverlayActive: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["click"],
    data() {
      return {
        expirationTimeClock: null,
        expirationTimeInSecondInternal: this.demand.getSecondsToExpiration(),
      };
    },
    computed: {
      contract() {
        switch (this.demand.getType()) {
          case DEMAND_TYPES.AE:
            return this.$t("CORE.MODELS.CONTRACTS.@AE");
          case DEMAND_TYPES.INTERIM:
            return this.$t("CORE.MODELS.CONTRACTS.@INTERIM");
          case DEMAND_TYPES.CDI:
            return this.$t("CORE.MODELS.CONTRACTS.@CDI");
          case DEMAND_TYPES.SEASON:
            return this.$t("CORE.MODELS.CONTRACTS.@SEASON");
          default:
            return "";
        }
      },
      contractStyle() {
        return {
          backgroundColor: this.color,
        };
      },
      qualificationStyle() {
        const rgba = colors.hexToRgb(this.color);
        return {
          backgroundColor: `rgba(${rgba.r},${rgba.g},${rgba.b}, 0.1)`,
        };
      },
      demandCardStyle() {
        return {
          borderTop: `4px solid ${this.demandColor}`,
          borderBottom: `4px solid ${this.demandColor}`,
        };
      },
      overlayStyle() {
        const rgba = colors.hexToRgb(this.color);
        return {
          backgroundColor: `rgba(${rgba.r},${rgba.g},${rgba.b}, 0.65)`,
        };
      },
      demandExpirationText() {
        if (this.expirationTimeInSecondInternal > EXPIRATION_TIME_REAL_TIME_THRESHOLD) {
          const days = Math.floor(this.expirationTimeInSecondInternal / (24 * 3600));
          return this.$tc("ETAB.PAGES.RECRUITMENT.@EXPIRATION_DAYS", days);
        } else if (this.expirationTimeInSecondInternal > 0) {
          return this.$t("ETAB.PAGES.RECRUITMENT.@EXPIRATION_TIME", {
            time: DatesHelpers.convertSecondsToHHMMSS(this.expirationTimeInSecondInternal),
          });
        } else {
          // demand expired, no need of timer anymore
          clearInterval(this.expirationTimeClock);
          return this.$t("ETAB.PAGES.RECRUITMENT.@EXPIRED_DEMAND");
        }
      },
    },
    created() {
      this.initDemandExpiration();
    },
    methods: {
      initDemandExpiration() {
        if (this.expirationTimeInSecondInternal) {
          this.expirationTimeClock = setInterval(() => {
            this.expirationTimeInSecondInternal -= 1;
          }, 1000);
        }
      },
      showDetailsLink() {
        return this.demand.hasStarted() || this.overlayActivated;
      },
      openRecap() {
        const dashboardStore = useDashboardStore();
        dashboardStore[ACTION_DASHBOARD_OPEN_RECRUITMENT_RECAP_MODAL]({ recap: this.demand });
      },
      handleClick(event) {
        this.$emit("click");
        event.stopPropagation();
      },
    },
  };
</script>

<style lang="scss" scoped>
  .base-demand {
    display: flex;
    flex-direction: column;
    font-size: 14px;
    width: 100%;
    border-radius: 7px;
    background-color: white;
    &__header {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      &__top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        padding: 8px;
        &__chip {
          color: white;
          background-color: $secondary;
          padding: 2px 1.5rem;
          text-align: center;
          border-radius: 5px;
        }
      }

      &__qualification {
        font-size: 16px;
        line-height: 24px;
        text-align: center;
        width: 100%;
        font-family: Poppins, Helvetica, sans-serif;
      }
    }
    &__card {
      & {
        display: flex;
        flex-direction: column;
        padding: 0.5rem;
        min-height: 160px;
        position: relative;
        cursor: pointer;
      }
      .dashboard-recruitment-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        min-height: 160px;
        color: white;
        border-bottom-left-radius: 4px;
        border-bottom-right-radius: 4px;
        box-shadow: $card-shadow;
        text-align: center;
        padding: 40px 15px;
        z-index: 2;
        display: flex;
        flex-direction: column;
        backdrop-filter: blur(3px);
      }

      &__middle {
        display: flex;
        flex: 1;
      }
    }
  }
  .expiration-date {
    text-align: right;
    font-style: italic;
    font-size: 12px;
    color: $grey;
    margin: 4px 0 16px;
  }
  .link {
    text-align: right;
    font-style: italic;
    font-size: 12px;
    color: $grey;
    cursor: pointer;
    text-decoration: underline;
  }
</style>
